import React, { useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import Paginacion from '../components/Paginacion';
import { formatFechaHora } from '../utils/format';
import Exportar from '../components/reportes/Exportar';
import EncabezadoImpresion from '../components/reportes/EncabezadoImpresion';
import BotonAyuda from '../components/BotonAyuda';

// Excel: todas las páginas de la búsqueda actual, hasta este tope de registros.
const MAX_PAGINAS_EXCEL = 50;

export default function Auditoria() {
  const { empresaActiva } = useAuth();
  const [registros, setRegistros] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [pagina, setPagina] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    if (!empresaActiva) return;
    setPagina(1);
  }, [empresaActiva, busqueda]);

  useEffect(() => {
    if (!empresaActiva) return;
    setCargando(true);
    const controlador = setTimeout(() => {
      api.get('/auditoria', { params: { q: busqueda || undefined, pagina } })
        .then((res) => {
          setRegistros(res.data.datos);
          setTotalPaginas(res.data.totalPaginas);
          setTotal(res.data.total);
        })
        .finally(() => setCargando(false));
    }, 300);
    return () => clearTimeout(controlador);
  }, [empresaActiva, busqueda, pagina]);

  async function contenidoExcel() {
    const todos = [];
    let pag = 1;
    let paginas = 1;
    do {
      const { data } = await api.get('/auditoria', { params: { q: busqueda || undefined, pagina: pag } });
      todos.push(...data.datos);
      paginas = data.totalPaginas;
      pag += 1;
    } while (pag <= paginas && pag <= MAX_PAGINAS_EXCEL);
    const cortado = todos.length < total;
    return {
      titulo: 'Bitácora de auditoría',
      detalle: [busqueda && `Búsqueda "${busqueda}"`, cortado && `los ${todos.length} registros más recientes de ${total}`].filter(Boolean).join(' · ') || 'Todos los registros',
      hojas: [{
        nombre: 'Auditoría',
        columnas: [{ titulo: 'Fecha y hora' }, { titulo: 'Usuario' }, { titulo: 'Acción' }, { titulo: 'Entidad' }, { titulo: 'Descripción' }, { titulo: 'IP' }],
        filas: todos.map((r) => [formatFechaHora(r.creado_en), r.usuario_nombre || r.usuario_email || '', r.accion, r.entidad || '', r.descripcion || '', r.ip || ''])
      }]
    };
  }

  return (
    <div className="space-y-6">
      <EncabezadoImpresion titulo="Bitácora de auditoría" detalle={`${busqueda ? `Búsqueda "${busqueda}" · ` : ''}página ${pagina} de ${totalPaginas}; el listado completo está en Exportar → Excel`} />
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between print:hidden">
        <h1 className="text-2xl font-bold text-slate-800">Bitácora de auditoría<BotonAyuda /></h1>
        <Exportar excel={contenidoExcel} disabled={cargando || registros.length === 0} />
      </div>
      <p className="text-sm text-slate-500 print:hidden">
        Registro de creaciones, ediciones y eliminaciones realizadas en el sistema (visible solo para gerentes y administradores).
      </p>

      <div className="relative max-w-md print:hidden">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          placeholder="Buscar por usuario, palabra clave o fecha..."
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          className="border rounded-md pl-9 pr-3 py-2 w-full text-sm"
        />
      </div>

      {cargando ? (
        <p className="text-slate-500">Cargando...</p>
      ) : registros.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center text-slate-400">
          {busqueda ? 'No se encontraron registros para tu búsqueda.' : 'Aún no hay actividad registrada en esta empresa.'}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm">
          <div className="divide-y">
            {registros.map((r) => (
              <div key={r.id} className="p-4">
                <p className="text-sm text-slate-700">
                  <span className="text-slate-400">
                    {formatFechaHora(r.creado_en)}
                    {' — '}
                  </span>
                  {r.descripcion || r.accion}
                </p>
              </div>
            ))}
          </div>
          <Paginacion pagina={pagina} totalPaginas={totalPaginas} total={total} onCambiarPagina={setPagina} />
        </div>
      )}
    </div>
  );
}
